import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ReactNode,
} from "react";
import { classSet } from "@/javascript/lib/helpers";
import type { IconProps } from "./icon.component";
import { Spinner } from "./spinner.component";

/**
 * Button Component
 *
 * A embryo that will be evolved into a full featured button component.
 * For now it supports variants and can render as a button or a link.
 */

export function Button({
  children,
  variant = "primary",
  type = "button",
  as = "button",
  icon,
  iconVariant,
  iconPosition = "leading",
  size = "medium",
  destructive = false,
  href,
  className,
  iconOnly = false,
  loading = false,
  ...props
}: ButtonProps) {
  const classNames = classSet(
    ...getButtonClassNames(
      variant,
      size,
      icon,
      iconPosition,
      destructive,
      iconOnly,
      loading
    ),
    className
  );
  const ariaProps = {
    ...(iconOnly ? { "aria-label": children as string } : {}),
    "aria-busy": loading || undefined,
  };
  const iconElement = loading ? (
    <Spinner size="small" className="puts-button__icon" aria-hidden />
  ) : icon ? (
    <i
      className={`puts-button__icon fa${iconVariant ? `-${iconVariant}` : ""} fa-${icon}`}
    />
  ) : null;
  const content = iconOnly ? (
    iconElement
  ) : (
    <>
      {iconElement}
      <span className="puts-button__text">{children}</span>
    </>
  );

  if (as === "a") {
    return (
      <a
        className={classNames}
        href={loading ? undefined : href}
        aria-disabled={loading || undefined}
        {...(props as ButtonAsLinkProps)}
        {...ariaProps}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      className={classNames}
      type={type}
      {...(props as ButtonAsButtonProps)}
      {...ariaProps}
      disabled={(props as ButtonAsButtonProps).disabled || loading}
    >
      {content}
    </button>
  );
}

export function getButtonClassNames(
  variant: CommonButtonProps["variant"],
  size: CommonButtonProps["size"],
  icon?: CommonButtonProps["icon"],
  iconPosition?: CommonButtonProps["iconPosition"],
  destructive?: CommonButtonProps["destructive"],
  iconOnly?: boolean,
  loading?: boolean
) {
  const classNames = [
    "puts-button",
    `puts-button--variant-${variant}`,
    `puts-button--size-${size}`,
  ];
  if ((icon || loading) && iconPosition) {
    classNames.push(`puts-button--icon-position-${iconPosition}`);
  }
  if (destructive) {
    classNames.push("puts-button--destructive");
  }
  if (iconOnly) {
    classNames.push("puts-button--icon-only");
  }
  if (loading) {
    classNames.push("puts-button--loading");
  }
  return classNames;
}

type IconOnlyProps = {
  iconOnly: true;
  icon: string;
  children: string;
};

type NotIconOnlyProps = {
  iconOnly?: false;
};

export type CommonButtonProps = (IconOnlyProps | NotIconOnlyProps) & {
  variant?: "primary" | "secondary" | "tertiary" | "link";
  icon?: string;
  iconVariant?: IconProps["variant"];
  iconPosition?: "leading" | "trailing";
  size?: "small" | "medium" | "large";
  destructive?: boolean;
  loading?: boolean;
  children?: ReactNode;
};

export type ButtonAsButtonProps = CommonButtonProps &
  Override<
    ButtonHTMLAttributes<HTMLButtonElement>,
    {
      as?: "button";
      type?: "button" | "submit" | "reset";
      href?: never;
    }
  >;

type ButtonAsLinkProps = CommonButtonProps &
  Override<
    AnchorHTMLAttributes<HTMLAnchorElement>,
    {
      as: "a";
      href?: string;
      type?: never;
    }
  >;

export type ButtonProps = ButtonAsButtonProps | ButtonAsLinkProps;
